import "./Accueil.css";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLayoutEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function Offre() {
  const { t } = useTranslation();
  const firstListRef = useRef(null);
  const secondListRef = useRef(null);
  const titleRef = useRef(null);
  const containerRef = useRef(null);

  // Animation de révélation progressive du texte basée sur le scroll
  useEffect(() => {
    const h2 = titleRef.current;
    if (!h2) return;

    // Vérification du support d'IntersectionObserver et prefers-reduced-motion
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      // Fallback : afficher le texte directement sans animation
      return;
    }

    // 1. Extraire le texte original et le conserver pour l'accessibilité
    const originalText = h2.textContent;

    // 2. Créer un conteneur caché pour les lecteurs d'écran avec le texte complet
    const srText = document.createElement("span");
    srText.className = "sr-only";
    srText.textContent = originalText;
    srText.setAttribute("aria-hidden", "false");

    // 3. Fractionner le texte en mots et créer des spans
    const words = originalText.split(/\s+/);
    const wordSpans = words.map((word, index) => {
      const span = document.createElement("span");
      span.className = "word-reveal";
      span.setAttribute("aria-hidden", "true");
      span.style.setProperty("--word-index", index);

      // Fractionner chaque mot en caractères pour l'effet de coloration progressive
      const chars = word.split("");
      chars.forEach((char, charIndex) => {
        const charSpan = document.createElement("span");
        charSpan.className = "char-reveal";
        charSpan.textContent = char;
        charSpan.style.setProperty("--char-index", charIndex);
        span.appendChild(charSpan);
      });

      return span;
    });

    // 4. Vider le h2 et injecter les spans + texte pour accessibilité
    h2.innerHTML = "";
    h2.appendChild(srText);
    wordSpans.forEach((span, index) => {
      h2.appendChild(span);
      // Ajouter un espace après chaque mot sauf le dernier
      if (index < wordSpans.length - 1) {
        h2.appendChild(document.createTextNode(" "));
      }
    });

    // 5. Utiliser GSAP ScrollTrigger pour animer la coloration progressive
    // La ligne atteint 100% quand la section est à 50% de scroll
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top 80%",
        end: "center center", // La ligne est complète quand le centre de la section atteint le centre du viewport (50%)
        scrub: 1,
        toggleActions: "play reverse play reverse",
        onUpdate: (self) => {
          // Calculer le pourcentage de progression
          const progress = self.progress;
          const totalChars = h2.querySelectorAll(".char-reveal").length;
          const charsToReveal = Math.floor(progress * totalChars);

          // Appliquer la classe 'is-visible' aux caractères en fonction du scroll
          h2.querySelectorAll(".char-reveal").forEach((char, index) => {
            if (index < charsToReveal) {
              char.classList.add("is-visible");
            } else {
              char.classList.remove("is-visible");
            }
          });
        },
      },
    });

    // 6. Nettoyer à la destruction du composant
    return () => {
      tl.kill();
      if (h2) {
        h2.innerHTML = originalText;
      }
    };
  }, [t]);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Animation de la première liste (monte avec stagger)
      gsap.fromTo(
        firstListRef.current.children,
        {
          y: 30,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power2.out",
          stagger: 0.1,
          scrollTrigger: {
            trigger: firstListRef.current,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Animation de la deuxième liste (monte avec stagger, délai)
      gsap.fromTo(
        secondListRef.current.children,
        {
          y: 30,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power2.out",
          stagger: 0.1,
          delay: 0.3,
          scrollTrigger: {
            trigger: secondListRef.current,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      );
    });

    return () => ctx.revert();
  }, []);

  return (
    <div className="Accueil-Offre" ref={containerRef}>
      <h2 ref={titleRef} id="offre-title">
        {t("offre.title")}
      </h2>

      <article className="Offre-list">
        <p ref={firstListRef}>
          <span>{t("offre.list1.dev")}</span>
          <span>{t("offre.list1.uiux")}</span>
          <span>{t("offre.list1.seo")}</span>
          <span>{t("offre.list1.telecom")}</span>
          <span>{t("offre.list1.it")}</span>
          <span>{t("offre.list1.cyber")}</span>
          <span>{t("offre.list1.infogerance")}</span>
        </p>
        <p ref={secondListRef}>
          <span>{t("offre.list2.artDirection")}</span>
          <span>{t("offre.list2.brand")}</span>
          <span>{t("offre.list2.identity")}</span>
          <span>{t("offre.list2.social")}</span>
          <span>{t("offre.list2.content")}</span>
          <span>{t("offre.list2.storage")}</span>
          <span>{t("offre.list2.cloud")}</span>
        </p>
      </article>
    </div>
  );
}
